import React, { useState, useContext } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useRecruitmentDashboard } from "../../contexts/RecruitmentDashboardContext";
import Table from "../../components/UI/Table";
import RecruitmentSummaryChart from "../../components/UI/LineChart";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { useDepartment } from "../../contexts/DepartmentMasterContext";

const RecruitmentDashboard = React.memo(() => {
  const { departments, isLoading, pagination } = useDepartment();
  const {
    dashboardData,
    loading,
    error,
    fetchLatestCampaignsPage,
    fetchCandidatesPage,
  } = useRecruitmentDashboard();
  const { roles } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("Jobs"); // Default tab is 'Jobs'
  const [selectedYear, setSelectedYear] = useState("2025");

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // console.log("dashboard data: ", dashboardData)
  console.log("Department Names : ", departments);
  // For pagination
  const jobs = dashboardData.jobs;
  const latestCampaigns = dashboardData.latest_campaigns;
  const paginatedCandidates = dashboardData.candidates;

  // Table headers for each tab
  const headers = {
    Jobs: [
      "Campaign Title",
      "Total Positions",
      "Applications",
      "Interviewed",
      "Selected",
      "Rejected",
    ],
    Candidates: ["Candidate Name", "Status", "Job"],
    Onboardings: ["Employee Name", "Status", "Job"],
  };

  // Render logic for rows based on active tab
  const renderRow = {
    Jobs: (job, index) => (
      // console.log("jobs", job),
      <tr
        key={index}
        className="border-b dark:border-gray-700 dark:bg-gray-950 border-gray-200 hover:bg-gray-50"
      >
        <td className="px-4 py-2 dark:text-white">{job["campaign_name"]}</td>
        <td className="px-4 py-2 dark:text-white">
          {job["total_positions"] || "-"}
        </td>
        <td className="px-4 py-2 dark:text-white">
          {job["applications"] || "-"}
        </td>
        <td className="px-4 py-2 dark:text-white">
          {job["interviewed"] || "-"}
        </td>
        <td className="px-4 py-2 dark:text-white">{job["selected"] || "-"}</td>
        {/* <td className="px-4 py-2 dark:text-white">{job["Feedback Pending"]}</td> */}
        <td className="px-4 py-2 dark:text-white">{job["rejected"] || "-"}</td>
        {/* <td className="px-4 py-2 dark:text-white">{job["onboarded"] || "-"}</td> */}
      </tr>
    ),
    Candidates: (candidate, index) => (
      <tr
        key={index}
        className="border-b dark:border-gray-700 dark:bg-gray-950 border-gray-200 hover:bg-gray-50"
      >
        <td className="px-4 py-2 dark:text-white">
          {candidate.candidate_name}
        </td>
        <td className="px-4 py-2 dark:text-white">{candidate.status}</td>
        <td className="px-4 py-2 dark:text-white">{candidate.job}</td>
      </tr>
    ),
    Onboardings: (onboarding, index) => (
      <tr
        key={index}
        className="border-b dark:border-gray-700 dark:bg-gray-950 border-gray-200 hover:bg-gray-50"
      >
        <td className="px-4 py-2 dark:text-white">
          {onboarding.employee_name}
        </td>
        <td className="px-4 py-2 dark:text-white">{onboarding.status}</td>
        <td className="px-4 py-2 dark:text-white">{onboarding.job}</td>
      </tr>
    ),
  };

  // Handle error display
  if (error) {
    return <div className="text-red-500 text-center py-4">{error}</div>;
  }

  return (
    <div className="px-3 py-2 font-poppins">
      <header className="mt-2 mb-2 flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Recruitment Dashboard
        </h1>
      </header>

      {/* Job Cards */}
      <div className="flex gap-3 mb-6 overflow-auto">
        {/* Job card 1 */}
        {dashboardData.top_campaigns.map((job, index) => (
          <div
            key={index}
            className="w-full flex flex-col gap-y-3 p-2 bg-white dark:bg-gray-900 rounded-lg shadow dark:border-gray-700 border border-gray-200"
          >
            <h2 className="text-sm font-semibold mb-2 truncate text-center dark:text-white">
              {job.campaign_name}
            </h2>
            <div className="flex justify-between items-center mb-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-semibold dark:text-white">
                  {job.Applications}
                </p>
                <p className="text-sm text-gray-500 dark:text-white">
                  Applications
                </p>
              </div>
              <div className="h-10 w-px bg-gray-300"></div>
              <div className="text-center">
                <p className="text-2xl font-semibold dark:text-white">
                  {job.Interviewed}
                </p>
                <p className="text-sm text-gray-500 dark:text-white">
                  Interviewed
                </p>
              </div>
              <div className="h-10 w-px bg-gray-300"></div>
              <div className="text-center">
                <p className="text-2xl font-semibold dark:text-white">
                  {job.Selected}
                </p>
                <p className="text-sm text-gray-500 dark:text-white">
                  Selected
                </p>
              </div>
            </div>
            <Link
              to={`${getRoleBasedRoute(
                "/admin/campaigns",
                roles
              )}?campaign_id=${job.id}`}
              className="w-full text-center py-2 px-4 bg-[#0077fe] text-white rounded hover:bg-[#266cbd] text-sm"
            >
              See Details
            </Link>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto lg:overflow-visible">
        <div className="bg-white dark:border-gray-700 dark:bg-gray-900 rounded-lg p-4 shadow-sm border border-gray-200 min-w-[500px] lg:min-w-0">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recruitment Summary
            </h2>
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="text-sm text-black dark:bg-gray-950 dark:border-gray-700 dark:text-white bg-[#EFF3F8] border border-[#DEDEDE] rounded px-2 py-1"
                value={selectedYear}
                onChange={handleYearChange}
              >
                <option value="2025">This year</option>
                <option value="2024">Last year</option>
                <option value="2023">2023</option>
              </select>
            </div>
          </div>
          <div className="h-auto">
            {/* <RecruitmentSummaryChart selectedYear={selectedYear} chartData={[]} /> */}
            <RecruitmentSummaryChart selectedYear={selectedYear} />
            <div className="flex items-center justify-center flex-wrap gap-4 mt-4">
              {/* one */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-[#10B981]"></div>
                <span className="text-sm text-gray-600 dark:text-white">
                  Applications Received
                </span>
              </div>
              {/* two */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-[#3B82F6]"></div>
                <span className="text-sm text-gray-600 dark:text-white">
                  Job Opened
                </span>
              </div>
              {/* three */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-[#F59E0B]"></div>
                <span className="text-sm text-gray-600 dark:text-white">
                  Shortlisted
                </span>
              </div>
              {/* four */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-[#EF4444]"></div>
                <span className="text-sm text-gray-600 dark:text-white">
                  Interviewed
                </span>
              </div>
              {/* five */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-[#8B5CF6]"></div>
                <span className="text-sm text-gray-600 dark:text-white">
                  Onboarded
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="bg-white dark:bg-gray-900 dark:border-gray-700 p-4 sm:p-6 rounded-lg shadow mt-6">
        {/* Tab Navigation */}
        <div className="flex flex-wrap sm:flex-nowrap whitespace-nowrap overflow-x-auto md:text-lg text-xs border-b border-gray-200 mb-4">
          {["Jobs" /*, "Candidates", "Onboardings"*/].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 sm:px-6 py-2 font-semibold transition-all duration-200 whitespace-nowrap ${
                activeTab === tab
                  ? "text-blue-600 dark:text-white border-b-2 border-blue-600"
                  : "text-gray-600 hover:text-blue-600 dark:text-gray-500"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Table Section */}
        {loading ? (
          <Skeleton
            count={5}
            height={40}
            baseColor={
              localStorage.getItem("theme") === "dark" ? "#374151" : "#e0e0e0"
            } // Tailwind gray-700
            highlightColor={
              localStorage.getItem("theme") === "dark" ? "#4b5563" : "#f5f5f5"
            } // Tailwind gray-600
            containerClassName={
              localStorage.getItem("theme") === "dark" ? "bg-gray-800" : ""
            }
            className={
              localStorage.getItem("theme") === "dark" ? "bg-gray-700" : ""
            }
          />
        ) : (
          <Table
            headers={headers[activeTab]}
            data={
              activeTab === "Jobs"
                ? jobs || []
                : activeTab === "Candidates"
                ? paginatedCandidates?.data || []
                : dashboardData[activeTab.toLowerCase()] || []
            }
            renderRow={renderRow[activeTab]}
          />
        )}

        {/* Pagination for Jobs tab */}
        {activeTab === "Jobs" &&
          latestCampaigns &&
          latestCampaigns.last_page > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(event) =>
                fetchLatestCampaignsPage(event.selected + 1)
              }
              forcePage={latestCampaigns.current_page - 1}
              pageRangeDisplayed={5}
              disabledClassName='opacity-50 cursor-not-allowed'
              disabledLinkClassName='pointer-events-none'
              pageCount={latestCampaigns.last_page}
              previousLabel="< Prev"
              previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
              previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
              nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center mt-4 font-poppins"
              pageClassName="mx-1 border rounded dark:text-white font-poppins"
              pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
              activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
            />
          )}

        {/* Pagination for Candidates tab */}
        {activeTab === "Candidates" &&
          paginatedCandidates &&
          paginatedCandidates.last_page > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(event) => fetchCandidatesPage(event.selected + 1)}
              forcePage={paginatedCandidates.current_page - 1}
              pageRangeDisplayed={5}
              disabledClassName='opacity-50 cursor-not-allowed'
              disabledLinkClassName='pointer-events-none'
              pageCount={paginatedCandidates.last_page}
              previousLabel="< Prev"
              previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
              previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
              nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center mt-4 font-poppins"
              pageClassName="mx-1 border rounded dark:text-white font-poppins"
              pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
              activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
            />
          )}
      </div>
    </div>
  );
});

export default RecruitmentDashboard;
